import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  RefreshCw, Zap, Search, Check, X, AlertTriangle,
  Loader2, ChevronDown, ChevronUp, Copy, User,
  Clock, Activity, Wrench, ArrowRight, Settings, KeyRound
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_BADGE = {
  active:   { bg: "bg-emerald-500/15", text: "text-emerald-400", label: "active" },
  past_due: { bg: "bg-amber-500/15",   text: "text-amber-400",   label: "past_due" },
  canceled: { bg: "bg-zinc-700/40",    text: "text-zinc-500",    label: "canceled" },
  trialing: { bg: "bg-blue-500/15",    text: "text-blue-400",    label: "trialing" },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { bg: "bg-zinc-800", text: "text-zinc-500", label: status };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="ml-1 p-0.5 rounded text-zinc-700 hover:text-zinc-400 transition"
      title="Kopieren"
    >
      {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
    </button>
  );
}

function Mono({ children }) {
  return <code className="font-mono text-[11px] text-zinc-400 bg-zinc-800/60 px-1.5 py-0.5 rounded">{children}</code>;
}

// ─── Tab: Auto-Sync Kandidaten ────────────────────────────────────────────────
function CandidatesTab({ token }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncStates, setSyncStates] = useState({});
  const [expanded, setExpanded] = useState({});   // auth0_id → bool
  const [overrides, setOverrides] = useState({});  // auth0_id → {email, sub_id, cus_id}
  const [allResult, setAllResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/subscriptions/dev/sync-candidates", { headers });
      setCandidates(r.data.candidates || []);
    } catch { setCandidates([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  const setOverride = (auth0_id, key, val) =>
    setOverrides(p => ({ ...p, [auth0_id]: { ...(p[auth0_id] || {}), [key]: val } }));

  const syncOne = async (auth0_id) => {
    setSyncStates(p => ({ ...p, [auth0_id]: { loading: true, result: null } }));
    try {
      const r = await api.post(`/subscriptions/dev/sync/${auth0_id}`, {}, { headers });
      setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: r.data } }));
      if (r.data.found) setCandidates(prev => prev.filter(c => c.auth0_id !== auth0_id));
    } catch (e) {
      setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: { found: false, error: e?.response?.data?.detail || "Fehler" } } }));
    }
  };

  const syncByEmail = async (auth0_id) => {
    const ov = overrides[auth0_id] || {};
    const email = ov.email?.trim();
    if (!email) return;
    setSyncStates(p => ({ ...p, [auth0_id]: { loading: true, result: null } }));
    try {
      // Lookup Stripe by email, then apply
      const lookupRes = await api.post("/subscriptions/dev/stripe-lookup", { query: email }, { headers });
      const sub = lookupRes.data.subscriptions?.[0];
      const cus = lookupRes.data.customer;
      if (!sub && !cus) {
        setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: { found: false, error: `Keine Stripe-Daten für "${email}" gefunden` } } }));
        return;
      }
      const applyRes = await api.post("/subscriptions/dev/manual-apply", {
        auth0_id,
        stripe_subscription_id: sub?.id || null,
        stripe_customer_id: cus?.id || null,
      }, { headers });
      setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: { found: true, subscription_id: sub?.id, customer_id: cus?.id, status: sub?.status } } }));
      if (sub?.id) setCandidates(prev => prev.filter(c => c.auth0_id !== auth0_id));
    } catch (e) {
      setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: { found: false, error: e?.response?.data?.detail || "Fehler" } } }));
    }
  };

  const applyManual = async (auth0_id) => {
    const ov = overrides[auth0_id] || {};
    if (!ov.sub_id && !ov.cus_id) return;
    setSyncStates(p => ({ ...p, [auth0_id]: { loading: true, result: null } }));
    try {
      await api.post("/subscriptions/dev/manual-apply", {
        auth0_id,
        stripe_subscription_id: ov.sub_id?.trim() || null,
        stripe_customer_id: ov.cus_id?.trim() || null,
      }, { headers });
      setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: { found: true, subscription_id: ov.sub_id, customer_id: ov.cus_id } } }));
      if (ov.sub_id) setCandidates(prev => prev.filter(c => c.auth0_id !== auth0_id));
    } catch (e) {
      setSyncStates(p => ({ ...p, [auth0_id]: { loading: false, result: { found: false, error: e?.response?.data?.detail || "Fehler" } } }));
    }
  };

  const syncAll = async () => {
    setSyncingAll(true); setAllResult(null);
    try {
      const r = await api.post("/subscriptions/dev/sync-all", {}, { headers });
      setAllResult(r.data);
      await load();
    } catch (e) {
      setAllResult({ error: e?.response?.data?.detail || "Fehler" });
    } finally { setSyncingAll(false); }
  };

  const inputCls = "bg-zinc-800 text-white rounded-lg px-3 py-1.5 text-xs outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-purple-500/40 transition placeholder:text-zinc-600 font-mono w-full";

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-zinc-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-white font-bold">{candidates.length} Supporter ohne Subscription-ID</p>
          <p className="text-zinc-600 text-xs mt-0.5">Kündigen-Button wird für diese User nicht angezeigt</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-xl bg-zinc-800 text-zinc-400 hover:text-white transition" title="Aktualisieren">
            <RefreshCw size={14} />
          </button>
          {candidates.length > 0 && (
            <button onClick={syncAll} disabled={syncingAll}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-bold transition">
              {syncingAll ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              Alle synchronisieren ({candidates.length})
            </button>
          )}
        </div>
      </div>

      {allResult && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-xs font-medium flex items-center gap-2 ${allResult.error ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"}`}>
          {allResult.error
            ? <><AlertTriangle size={13} /> {allResult.error}</>
            : <><Check size={13} /> {allResult.synced}/{allResult.total} synchronisiert, {allResult.failed} fehlgeschlagen</>
          }
        </div>
      )}

      {candidates.length === 0 ? (
        <div className="text-center py-16">
          <Check size={32} className="text-emerald-400 mx-auto mb-3" />
          <p className="text-white font-bold">Alle Supporter sind synchronisiert</p>
          <p className="text-zinc-600 text-xs mt-1">Keine fehlenden Stripe-IDs gefunden</p>
        </div>
      ) : (
        <div className="space-y-2">
          {candidates.map(c => {
            const state = syncStates[c.auth0_id] || {};
            const isExpanded = expanded[c.auth0_id];
            const ov = overrides[c.auth0_id] || {};
            const syncFailed = state.result && !state.result.found;

            return (
              <div key={c.auth0_id} className="bg-zinc-900/50 rounded-xl overflow-hidden border border-white/[0.04]">
                {/* Hauptzeile */}
                <div className="flex items-center gap-4 px-4 py-3">
                  <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <User size={14} className="text-zinc-600" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className="text-white text-sm font-semibold truncate">{c.username || c.email}</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-zinc-600 text-xs">{c.email}</span>
                      {c.supporter_plan && <span className="text-zinc-700 text-xs">{c.supporter_plan}</span>}
                      {c.stripe_customer_id
                        ? <span className="flex items-center gap-0.5"><Mono>{c.stripe_customer_id}</Mono><CopyBtn value={c.stripe_customer_id} /></span>
                        : <span className="text-[10px] text-amber-500/70">Keine customer_id</span>
                      }
                    </div>
                  </div>

                  {/* Ergebnis */}
                  {state.result && (
                    <div className={`text-xs flex items-center gap-1.5 font-medium flex-shrink-0 ${state.result.found ? "text-emerald-400" : "text-red-400"}`}>
                      {state.result.found
                        ? <><Check size={12} /> Synced</>
                        : <><X size={12} /> {state.result.error?.length > 30 ? "Nicht gefunden" : (state.result.error || "Nicht gefunden")}</>
                      }
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => syncOne(c.auth0_id)} disabled={state.loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs font-semibold transition disabled:opacity-40">
                      {state.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      Sync
                    </button>
                    {/* Expand-Toggle bei Fehler oder manuell */}
                    <button
                      onClick={() => setExpanded(p => ({ ...p, [c.auth0_id]: !p[c.auth0_id] }))}
                      className={`p-1.5 rounded-lg transition text-xs ${isExpanded ? "bg-zinc-700 text-white" : "bg-zinc-800 text-zinc-500 hover:text-white"}`}
                      title="Manuell eingeben"
                    >
                      {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>

                {/* Expanded: manuelle Eingabe */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/[0.05] space-y-3">
                    {/* Option A: andere E-Mail suchen */}
                    <div>
                      <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-semibold mb-1.5">
                        Option A – Per abweichender E-Mail in Stripe suchen
                      </p>
                      <div className="flex gap-2">
                        <input
                          value={ov.email || ""}
                          onChange={e => setOverride(c.auth0_id, "email", e.target.value)}
                          placeholder="andere@email.com"
                          className={inputCls}
                        />
                        <button
                          onClick={() => syncByEmail(c.auth0_id)}
                          disabled={state.loading || !ov.email?.trim()}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:opacity-40 text-white text-xs font-bold transition"
                        >
                          {state.loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                          Suchen & Anwenden
                        </button>
                      </div>
                    </div>

                    {/* Option B: direkt IDs eintragen */}
                    <div>
                      <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-semibold mb-1.5">
                        Option B – Stripe-IDs direkt eintragen
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                        <input
                          value={ov.sub_id || ""}
                          onChange={e => setOverride(c.auth0_id, "sub_id", e.target.value)}
                          placeholder="sub_xxxxxxxxxxxxxxxxxx"
                          className={inputCls}
                        />
                        <input
                          value={ov.cus_id || ""}
                          onChange={e => setOverride(c.auth0_id, "cus_id", e.target.value)}
                          placeholder="cus_xxxxxxxxxxxxxxxxxx (optional)"
                          className={inputCls}
                        />
                      </div>
                      <button
                        onClick={() => applyManual(c.auth0_id)}
                        disabled={state.loading || (!ov.sub_id?.trim() && !ov.cus_id?.trim())}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 disabled:opacity-40 text-white text-xs font-bold transition"
                      >
                        {state.loading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        IDs speichern
                      </button>
                    </div>

                    {syncFailed && (
                      <p className="text-red-400/70 text-xs flex items-center gap-1.5">
                        <AlertTriangle size={11} /> {state.result.error}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Stripe Lookup ───────────────────────────────────────────────────────
function LookupTab({ token }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [applyAuth0Id, setApplyAuth0Id] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const lookup = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(""); setResult(null); setApplyResult(null);
    try {
      const r = await api.post("/subscriptions/dev/stripe-lookup", { query: query.trim() }, { headers });
      setResult(r.data);
      // Auth0-ID vorausfüllen wenn DB-User gefunden
      if (r.data?.db_user?.auth0_id) {
        setApplyAuth0Id(r.data.db_user.auth0_id);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };

  const apply = async (subId, custId) => {
    if (!applyAuth0Id.trim()) return;
    setApplying(true); setApplyResult(null);
    try {
      const r = await api.post("/subscriptions/dev/manual-apply", {
        auth0_id: applyAuth0Id.trim(),
        stripe_subscription_id: subId || null,
        stripe_customer_id: custId || null,
      }, { headers });
      setApplyResult({ ok: true, msg: `Gespeichert: ${r.data.updated.join(", ")}` });
    } catch (e) {
      setApplyResult({ ok: false, msg: e?.response?.data?.detail || "Fehler" });
    } finally { setApplying(false); }
  };

  return (
    <div>
      {/* Search */}
      <div className="flex gap-2 mb-6">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === "Enter" && lookup()}
          placeholder="E-Mail, sub_xxx, cus_xxx …"
          className="flex-1 bg-zinc-900 text-white rounded-xl px-4 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-purple-500/40 transition placeholder:text-zinc-600"
        />
        <button onClick={lookup} disabled={loading || !query.trim()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold transition">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 text-red-400 text-sm mb-4">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          {/* Customer */}
          {result.customer ? (
            <div className="bg-zinc-900/50 rounded-xl p-4">
              <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-semibold mb-3">Stripe Kunde</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 text-xs w-20 flex-shrink-0">Customer ID</span>
                  <Mono>{result.customer.id}</Mono><CopyBtn value={result.customer.id} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 text-xs w-20 flex-shrink-0">E-Mail</span>
                  <span className="text-white text-xs">{result.customer.email}</span>
                </div>
                {result.customer.name && (
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-600 text-xs w-20 flex-shrink-0">Name</span>
                    <span className="text-white text-xs">{result.customer.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 text-xs w-20 flex-shrink-0">Erstellt</span>
                  <span className="text-zinc-400 text-xs">{new Date(result.customer.created).toLocaleDateString("de-DE")}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 text-amber-400 text-sm">
              <AlertTriangle size={14} /> Kein Stripe-Kunde gefunden
            </div>
          )}

          {/* DB User Vergleich */}
          {result.db_user ? (
            <div className="bg-zinc-900/50 rounded-xl p-4 border border-sky-500/20">
              <p className="text-sky-400 text-[11px] uppercase tracking-wider font-semibold mb-3">DB-Eintrag (HUWND)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 w-28 flex-shrink-0">Username</span>
                  <span className="text-white font-semibold">{result.db_user.username || "–"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 w-28 flex-shrink-0">Auth0 ID</span>
                  <Mono>{result.db_user.auth0_id}</Mono><CopyBtn value={result.db_user.auth0_id} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 w-28 flex-shrink-0">Supporter</span>
                  <span className={result.db_user.is_supporter ? "text-emerald-400 font-bold" : "text-red-400"}>
                    {result.db_user.is_supporter ? "Ja" : "Nein"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 w-28 flex-shrink-0">Plan (DB)</span>
                  <span className={result.db_user.supporter_plan ? "text-white" : "text-zinc-600"}>
                    {result.db_user.supporter_plan || "nicht gesetzt"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 w-28 flex-shrink-0">Subscription ID</span>
                  {result.db_user.stripe_subscription_id
                    ? <><Mono>{result.db_user.stripe_subscription_id}</Mono><CopyBtn value={result.db_user.stripe_subscription_id} /></>
                    : <span className="text-zinc-600">nicht gesetzt</span>
                  }
                </div>
                {result.db_user.given_supporter_by && (
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-600 w-28 flex-shrink-0">Manuell von</span>
                    <span className="text-amber-400">{result.db_user.given_supporter_by}</span>
                  </div>
                )}
              </div>
            </div>
          ) : result.customer ? (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 text-amber-400 text-sm">
              <AlertTriangle size={14} /> Kein HUWND-User für diesen Stripe-Kunden gefunden
            </div>
          ) : null}

          {/* Subscriptions */}
          {result.subscriptions?.length > 0 && (
            <div className="bg-zinc-900/50 rounded-xl p-4">
              <p className="text-zinc-500 text-[11px] uppercase tracking-wider font-semibold mb-3">Abonnements ({result.subscriptions.length})</p>
              <div className="space-y-3">
                {result.subscriptions.map(sub => (
                  <div key={sub.id} className="border border-white/[0.05] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Mono>{sub.id}</Mono>
                        <CopyBtn value={sub.id} />
                        <StatusBadge status={sub.status} />
                        {sub.cancel_at_period_end && <span className="text-[10px] text-amber-400 font-semibold">Kündigung läuft</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-600 flex-wrap">
                      {sub.current_period_end && <span>Läuft bis: {new Date(sub.current_period_end).toLocaleDateString("de-DE")}</span>}
                      {sub.plan && <span className="truncate max-w-[200px]">Plan: {sub.plan}</span>}
                    </div>

                    {/* Apply to User */}
                    <div className="mt-3 pt-3 border-t border-white/[0.05]">
                      <p className="text-zinc-600 text-xs mb-2">Auf User anwenden:</p>
                      <div className="flex gap-2">
                        <input
                          value={applyAuth0Id}
                          onChange={e => setApplyAuth0Id(e.target.value)}
                          placeholder="auth0|... oder google-oauth2|..."
                          className="flex-1 bg-zinc-800 text-white rounded-lg px-3 py-1.5 text-xs outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-purple-500/40 transition placeholder:text-zinc-600 font-mono"
                        />
                        <button
                          onClick={() => apply(sub.id, result.customer?.id)}
                          disabled={applying || !applyAuth0Id.trim()}
                          className="px-3 py-1.5 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:opacity-40 text-white text-xs font-bold transition"
                        >
                          {applying ? <Loader2 size={12} className="animate-spin" /> : "Anwenden"}
                        </button>
                      </div>
                      {applyResult && (
                        <p className={`mt-2 text-xs flex items-center gap-1.5 ${applyResult.ok ? "text-emerald-400" : "text-red-400"}`}>
                          {applyResult.ok ? <Check size={11} /> : <X size={11} />} {applyResult.msg}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.customer && result.subscriptions?.length === 0 && (
            <div className="px-4 py-3 rounded-xl bg-zinc-800 text-zinc-500 text-sm">
              Keine Abonnements für diesen Kunden gefunden
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Vollständiger Stripe-Scan ─────────────────────────────────────────
function FullScanTab({ token }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [discrepancyLoading, setDiscrepancyLoading] = useState(false);
  const [discrepancies, setDiscrepancies] = useState(null);
  const [fixLoading, setFixLoading] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const runScan = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await api.post("/subscriptions/dev/full-stripe-scan", {}, { headers });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Scan");
    } finally { setLoading(false); }
  };

  const loadDiscrepancies = async () => {
    setDiscrepancyLoading(true);
    try {
      const r = await api.get("/subscriptions/dev/payment-discrepancies", { headers });
      setDiscrepancies(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler");
    } finally { setDiscrepancyLoading(false); }
  };

  const fixPlans = async () => {
    setFixLoading(true); setFixResult(null);
    try {
      const r = await api.post("/subscriptions/dev/fix-supporter-plans", {}, { headers });
      setFixResult(r.data);
    } catch (e) {
      setFixResult({ error: e?.response?.data?.detail || "Fehler" });
    } finally { setFixLoading(false); }
  };

  return (
    <div className="space-y-6">
      {/* Erklärung */}
      <div className="bg-zinc-900 rounded-xl p-4 border border-amber-500/20">
        <p className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-1">Was macht dieser Scan?</p>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Lädt <strong className="text-white">alle aktiven Stripe-Abonnements</strong> und gleicht sie mit der DB ab.
          User die bezahlt haben aber nicht als Supporter markiert sind, werden automatisch aktiviert.
          Funktioniert auch wenn der Webhook nicht ankam oder der Redirect fehlschlug.
        </p>
      </div>

      {/* Scan Button */}
      <div>
        <button
          onClick={runScan}
          disabled={loading}
          data-testid="full-stripe-scan-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-sm transition"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
          {loading ? "Scanne Stripe..." : "Vollständigen Stripe-Scan starten"}
        </button>
        <p className="text-zinc-600 text-xs mt-2">Kann bei vielen Abos einige Sekunden dauern</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 text-red-400 text-sm">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Gescannt", value: result.total_scanned, color: "text-zinc-300" },
              { label: "Aktiviert/Korrigiert", value: result.fixed_count, color: "text-emerald-400" },
              { label: "Bereits OK", value: result.already_ok_count, color: "text-zinc-500" },
              { label: "Kein User", value: result.no_matching_user_count, color: "text-amber-400" },
            ].map(s => (
              <div key={s.label} className="bg-zinc-900/60 rounded-xl p-3 text-center">
                <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                <p className="text-zinc-600 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Plan-Korrekturen hervorheben */}
          {result.plan_corrections_count > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 text-sm">
              <Wrench size={14} />
              <span><strong>{result.plan_corrections_count}</strong> Plan-Korrekturen durchgeführt (falscher Plan in DB korrigiert)</span>
            </div>
          )}

          {/* Aktivierte / korrigierte User */}
          {result.fixed?.length > 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
              <p className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3">
                Aktiviert / Korrigiert ({result.fixed_count})
              </p>
              <div className="space-y-2">
                {result.fixed.map((u, i) => (
                  <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/[0.04] last:border-0">
                    <Check size={13} className="text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-white text-sm font-semibold">{u.username || u.email}</span>
                      <span className="text-zinc-600 text-xs ml-2">{u.email}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {u.plan_corrected_from ? (
                        <span className="text-xs flex items-center gap-1">
                          <span className="text-zinc-500 line-through">{u.plan_corrected_from}</span>
                          <ArrowRight size={10} className="text-zinc-600" />
                          <span className="text-sky-400 font-semibold">{u.plan}</span>
                        </span>
                      ) : (
                        u.plan && <span className="text-xs text-zinc-500">{u.plan}</span>
                      )}
                      <Mono>{u.subscription_id}</Mono>
                      <CopyBtn value={u.subscription_id} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keine Aktivierung nötig */}
          {result.fixed_count === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-zinc-800 text-zinc-400 text-sm">
              <Check size={14} className="text-emerald-400" />
              Alle Stripe-Subscriber sind bereits korrekt aktiviert
            </div>
          )}

          {/* User ohne DB-Eintrag */}
          {result.no_matching_users?.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <p className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                Keine passenden User in DB ({result.no_matching_user_count})
              </p>
              <p className="text-zinc-600 text-xs mb-3">Diese Stripe-Kunden sind nicht bei uns registriert</p>
              <div className="space-y-1">
                {result.no_matching_users.slice(0, 10).map((u, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                    <AlertTriangle size={11} className="text-amber-500/50" />
                    <span>{u.email || "Keine E-Mail"}</span>
                    <Mono>{u.customer_id}</Mono>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Plan-Fix: Bestehende User */}
      <div className="border-t border-white/[0.05] pt-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-white text-sm font-bold flex items-center gap-2">
              <Wrench size={14} className="text-zinc-400" /> Plan-Korrektur für bestehende Supporter
            </p>
            <p className="text-zinc-600 text-xs mt-0.5">
              Prüft alle Supporter mit Stripe-Subscription und setzt den korrekten Plan (monthly/yearly).
              Manuell zugewiesene Supporter werden übersprungen.
            </p>
          </div>
          <button
            onClick={fixPlans}
            disabled={fixLoading}
            data-testid="fix-supporter-plans-btn"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold transition disabled:opacity-40"
          >
            {fixLoading ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
            Pläne prüfen & korrigieren
          </button>
        </div>

        {fixResult && (
          fixResult.error ? (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-500/10 text-red-400 text-xs mb-3">
              <AlertTriangle size={12} /> {fixResult.error}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Geprüft", val: fixResult.total_checked, color: "text-zinc-300" },
                  { label: "Korrigiert", val: fixResult.fixed, color: "text-emerald-400" },
                  { label: "Bereits korrekt", val: fixResult.already_correct, color: "text-zinc-500" },
                ].map(s => (
                  <div key={s.label} className="bg-zinc-900/60 rounded-xl p-3 text-center">
                    <p className={`text-xl font-black ${s.color}`}>{s.val}</p>
                    <p className="text-zinc-600 text-[11px] mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {fixResult.details?.length > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 space-y-1.5 mt-2">
                  <p className="text-emerald-400 text-[11px] font-bold uppercase tracking-wider mb-2">Korrigiert</p>
                  {fixResult.details.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs">
                      <span className="text-white font-semibold w-32 truncate">{d.username || d.auth0_id}</span>
                      <span className="text-zinc-600 line-through">{d.old_plan}</span>
                      <ArrowRight size={10} className="text-zinc-600" />
                      <span className="text-emerald-400 font-semibold">{d.new_plan}</span>
                    </div>
                  ))}
                </div>
              )}
              {fixResult.errors > 0 && (
                <p className="text-amber-400 text-xs">{fixResult.errors} Fehler beim Abruf (Stripe-Probleme)</p>
              )}
            </div>
          )
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-white/[0.05] pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white text-sm font-bold">Bezahlte Checkouts ohne Aktivierung</p>
            <p className="text-zinc-600 text-xs mt-0.5">Findet User mit abgeschlossenem Checkout aber fehlender Supporter-Aktivierung. Bereits aktive User werden automatisch bereinigt.</p>
          </div>
          <button
            onClick={loadDiscrepancies}
            disabled={discrepancyLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold transition disabled:opacity-40"
          >
            {discrepancyLoading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Prüfen
          </button>
        </div>

        {discrepancies && (
          <div className="space-y-3">
            {/* Auto-bereinigt */}
            {discrepancies.auto_fixed_count > 0 && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs">
                <Check size={12} />
                <span><strong>{discrepancies.auto_fixed_count}</strong> bereits aktive Supporter wurden automatisch bereinigt (offene Transaktionen als "bezahlt" markiert)</span>
              </div>
            )}

            {discrepancies.count === 0 ? (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-zinc-800 text-zinc-500 text-xs">
                <Check size={12} className="text-emerald-400" /> Keine echten Diskrepanzen gefunden
              </div>
            ) : (
              <div className="space-y-2">
                {discrepancies.discrepancies.map((d, i) => (
                  <div key={i} className="bg-zinc-900/60 rounded-xl p-3 border border-red-500/20">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-semibold">{d.username || d.email}</span>
                      <span className="text-red-400 text-xs font-bold">Nicht aktiviert</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                      <span>{d.email}</span>
                      {d.plan && <span>{d.plan}</span>}
                      {d.stripe_subscription_id && <span className="font-mono">{d.stripe_subscription_id}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Cron-Job Logs ──────────────────────────────────────────────────────
function CronLogsTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/subscriptions/dev/cron-logs", { headers });
      setLogs(r.data.logs || []);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  const fmt = (iso) => {
    if (!iso) return "–";
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-zinc-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-white font-bold">Automatische Cron-Job Logs</p>
          <p className="text-zinc-600 text-xs mt-0.5">Täglich 00:05 (Aktivierung) und 00:10 (Ablauf) · letzte 50 Läufe</p>
        </div>
        <button onClick={load} className="p-2 rounded-xl bg-zinc-800 text-zinc-400 hover:text-white transition" title="Aktualisieren">
          <RefreshCw size={14} />
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-16">
          <Activity size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 font-bold">Noch keine Cron-Logs</p>
          <p className="text-zinc-700 text-xs mt-1">Logs erscheinen nach dem ersten automatischen Lauf (täglich 00:05)</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => {
            const isActivation = log.type === "activation";
            const hasActivity = isActivation
              ? (log.activated > 0 || log.plan_fixed > 0)
              : log.deactivated > 0;
            const hasError = log.errors > 0;

            return (
              <div key={i} className={`bg-zinc-900/50 border rounded-xl px-4 py-3 flex items-center gap-4 ${hasError ? "border-amber-500/15" : "border-white/[0.04]"}`}>
                {/* Icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActivation ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
                  {isActivation
                    ? <Zap size={14} className="text-emerald-400" />
                    : <Clock size={14} className="text-rose-400" />
                  }
                </div>

                {/* Typ + Zeit */}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold">
                    {isActivation ? "Aktivierungscheck" : "Ablaufcheck"}
                  </p>
                  <p className="text-zinc-600 text-xs">{fmt(log.ran_at)}</p>
                </div>

                {/* Ergebnisse */}
                <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                  {isActivation ? (
                    <>
                      {log.activated > 0 && (
                        <span className="flex items-center gap-1 text-emerald-400 font-semibold">
                          <Check size={11} /> {log.activated} aktiviert
                        </span>
                      )}
                      {log.plan_fixed > 0 && (
                        <span className="flex items-center gap-1 text-sky-400 font-semibold">
                          <Wrench size={11} /> {log.plan_fixed} Pläne
                        </span>
                      )}
                      {!hasActivity && (
                        <span className="text-zinc-600">Keine Änderungen</span>
                      )}
                    </>
                  ) : (
                    <>
                      {log.deactivated > 0 && (
                        <span className="flex items-center gap-1 text-rose-400 font-semibold">
                          <X size={11} /> {log.deactivated} deaktiviert
                        </span>
                      )}
                      {!hasActivity && (
                        <span className="text-zinc-600">Keine Ablaufenden</span>
                      )}
                    </>
                  )}
                  {hasError && (
                    <span className="flex items-center gap-1 text-amber-400">
                      <AlertTriangle size={11} /> {log.errors} Fehler
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Konfiguration ──────────────────────────────────────────────────────
function ConfigTab({ token }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    api.get("/subscriptions/dev/config", { headers })
      .then(r => setCfg(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-zinc-600" /></div>;
  if (!cfg) return <p className="text-red-400 text-sm">Fehler beim Laden</p>;

  const rows = [
    { label: "Monthly Price ID",  value: cfg.monthly_price_id, hint: "3€/Monat" },
    { label: "Yearly Price ID",   value: cfg.yearly_price_id,  hint: "20€/Jahr" },
    { label: "API Key",           value: cfg.api_key_hint,     hint: null, noCopy: true },
    { label: "Webhook Secret",    value: cfg.webhook_secret_set ? "✓ gesetzt" : "✗ NICHT gesetzt",
      hint: null, noCopy: true,
      valueClass: cfg.webhook_secret_set ? "text-emerald-400" : "text-red-400" },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
        {rows.map((row, i) => (
          <div key={row.label}
            className={`flex items-center gap-4 px-5 py-4 ${i < rows.length - 1 ? "border-b border-white/[0.05]" : ""}`}>
            <div className="w-40 flex-shrink-0">
              <p className="text-zinc-500 text-xs font-semibold">{row.label}</p>
              {row.hint && <p className="text-zinc-700 text-[11px] mt-0.5">{row.hint}</p>}
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <code className={`font-mono text-sm bg-zinc-800 px-3 py-1.5 rounded-lg truncate ${row.valueClass || "text-white"}`}>
                {row.value || <span className="text-zinc-600 italic">nicht gesetzt</span>}
              </code>
              {!row.noCopy && row.value && <CopyBtn value={row.value} />}
            </div>
          </div>
        ))}
      </div>
      <p className="text-zinc-700 text-xs flex items-center gap-1.5">
        <KeyRound size={11} /> Price IDs werden aus der <code className="bg-zinc-800 px-1 rounded text-zinc-500">.env</code> Datei gelesen – Änderungen erfordern Backend-Neustart.
      </p>
    </div>
  );
}

// ─── Hauptseite ───────────────────────────────────────────────────────────────
export default function StripeSyncPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState("candidates");

  const TABS = [
    { id: "candidates", label: "Sync-Kandidaten" },
    { id: "fullscan",   label: "Vollst. Scan" },
    { id: "lookup",     label: "Stripe Lookup" },
    { id: "cronlogs",   label: "Cron-Logs" },
    { id: "config",     label: "Konfiguration" },
  ];

  return (
    <AdminLayout>
      <Helmet><title>Stripe Sync | Developer</title></Helmet>
      <div className="p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-black text-white font-heading flex items-center gap-2">
            <Zap size={20} className="text-purple-400" />
            Stripe ↔ DB Sync
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Developer-Tool · Stripe-Daten mit der Datenbank abgleichen</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-900 border border-white/5 rounded-xl p-1 mb-6 w-fit">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === t.id ? "bg-purple-600 text-white" : "text-zinc-500 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-zinc-900 border border-white/5 rounded-xl p-5">
          {tab === "candidates" && <CandidatesTab token={token} />}
          {tab === "fullscan"   && <FullScanTab token={token} />}
          {tab === "lookup"     && <LookupTab token={token} />}
          {tab === "cronlogs"   && <CronLogsTab token={token} />}
          {tab === "config"     && <ConfigTab token={token} />}
        </div>
      </div>
    </AdminLayout>
  );
}
